import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MarketInsightsResponse } from '@/models/MarketInsights';
import { getMarketInsights, setCompStarred } from '@/services/api/market-insights/api';

export interface UseMarketInsightsResult {
  data: MarketInsightsResponse | null;
  isLoading: boolean;
  isError: boolean;
}

const queryKeyFor = (propertyId: string | undefined) => ['market-insights', propertyId];

export function useMarketInsights(propertyId: string | undefined): UseMarketInsightsResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeyFor(propertyId),
    queryFn: () => getMarketInsights(propertyId),
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
  });

  return { data: data ?? null, isLoading, isError };
}

/**
 * Optimistically flips a single comp's `starred` flag in the cached
 * response — no refetch needed, this is purely a client-visible toggle on
 * data the backend already returned. Rolls back on failure.
 */
export function useSetCompStarred(propertyId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = queryKeyFor(propertyId);

  return useMutation({
    mutationFn: ({ attomId, starred }: { attomId: string; starred: boolean }) =>
      setCompStarred({ propertyId: propertyId as string, attomId, starred }),
    onMutate: async ({ attomId, starred }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<MarketInsightsResponse>(queryKey);
      if (previous) {
        queryClient.setQueryData<MarketInsightsResponse>(queryKey, {
          ...previous,
          comparables: {
            ...previous.comparables,
            data: previous.comparables.data.map((comp) =>
              comp.attomId === attomId ? { ...comp, starred } : comp,
            ),
          },
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });
}
